/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Security Tab - Uses real game data for security metrics
 */

import React, { useMemo } from 'react';
import { CityStats, Grid, BuildingType, SimulationBreakdown } from '../../../types';
import { BUILDINGS } from '../../../constants';

interface SecurityTabProps {
  stats: CityStats;
  grid: Grid;
  simBreakdown?: SimulationBreakdown;
}

const SecurityTab: React.FC<SecurityTabProps> = ({ stats, grid, simBreakdown }) => {
  // Calculate real security metrics from grid
  const securityMetrics = useMemo(() => {
    const counts: Record<string, number> = {};
    let totalSecurityBuildings = 0;
    let cyberBuildings = 0;
    let zeroTrustBuildings = 0;
    let advancedSecurityBuildings = 0;

    grid.flat().forEach(tile => {
      if (tile.buildingType !== BuildingType.None && tile.isConnected) {
        counts[tile.buildingType] = (counts[tile.buildingType] || 0) + 1;
        const config = BUILDINGS[tile.buildingType];
        
        if (config.category === 'Security') {
          totalSecurityBuildings++;
        }
        if (config.category === 'Cyber') {
          cyberBuildings++;
        }
        if (config.category === 'ZeroTrust') {
          zeroTrustBuildings++;
        }
        if (config.category === 'EnhancedSecurity') {
          advancedSecurityBuildings++;
        }
      }
    });

    // Calculate threat mitigation
    const firewallCount = counts[BuildingType.Firewall] || 0;
    const siemCount = counts[BuildingType.SIEM] || 0;
    const edrCount = counts[BuildingType.EDR] || 0;
    const zeroTrustCount = counts[BuildingType.ZeroTrust] || 0;
    const wafCount = counts[BuildingType.WAF] || 0;
    const socCount = counts[BuildingType.SOC] || 0;
    const penTestCount = counts[BuildingType.PenTestLab] || 0;
    const threatIntelCount = counts[BuildingType.ThreatIntel] || 0;

    return {
      counts,
      totalSecurityBuildings,
      cyberBuildings,
      zeroTrustBuildings,
      advancedSecurityBuildings,
      firewallCount,
      siemCount,
      edrCount,
      zeroTrustCount,
      wafCount,
      socCount,
      penTestCount,
      threatIntelCount
    };
  }, [grid]);

  // Calculate threat level
  const threatLevel = useMemo(() => {
    if (stats.threat >= 75) return { level: 'CRITICAL', color: 'text-red-500', bg: 'from-red-900/30 to-red-800/30', border: 'border-red-700/50' };
    if (stats.threat >= 50) return { level: 'HIGH', color: 'text-orange-400', bg: 'from-orange-900/30 to-red-900/30', border: 'border-orange-700/50' };
    if (stats.threat >= 25) return { level: 'ELEVATED', color: 'text-yellow-400', bg: 'from-yellow-900/30 to-orange-900/30', border: 'border-yellow-700/50' };
    if (stats.threat >= 10) return { level: 'MODERATE', color: 'text-cyan-400', bg: 'from-cyan-900/30 to-blue-900/30', border: 'border-cyan-700/50' };
    return { level: 'LOW', color: 'text-emerald-400', bg: 'from-emerald-900/30 to-green-900/30', border: 'border-emerald-700/50' };
  }, [stats.threat]);

  // Security systems status based on actual buildings
  const securitySystems = useMemo(() => {
    const systems = [];
    
    if (securityMetrics.firewallCount > 0) {
      systems.push({ name: 'Firewall', status: 'Operational', count: securityMetrics.firewallCount, color: 'emerald' });
    }
    if (securityMetrics.siemCount > 0) {
      systems.push({ name: 'SIEM', status: 'Monitoring', count: securityMetrics.siemCount, color: 'emerald' });
    }
    if (securityMetrics.edrCount > 0) {
      systems.push({ name: 'EDR', status: 'Active', count: securityMetrics.edrCount, color: 'emerald' });
    }
    if (securityMetrics.wafCount > 0) {
      systems.push({ name: 'WAF', status: 'Filtering', count: securityMetrics.wafCount, color: 'emerald' });
    }
    if (securityMetrics.socCount > 0) {
      systems.push({ name: 'SOC', status: 'Watching', count: securityMetrics.socCount, color: 'cyan' });
    }
    if (securityMetrics.threatIntelCount > 0) {
      systems.push({ name: 'Threat Intel', status: 'Analyzing', count: securityMetrics.threatIntelCount, color: 'purple' });
    }
    if (securityMetrics.zeroTrustCount > 0) {
      systems.push({ name: 'Zero Trust', status: 'Enforcing', count: securityMetrics.zeroTrustCount, color: 'cyan' });
    }
    if (securityMetrics.penTestCount > 0) {
      systems.push({ name: 'PenTest Lab', status: 'Testing', count: securityMetrics.penTestCount, color: 'orange' });
    }

    // Add warnings for missing critical systems
    if (securityMetrics.firewallCount === 0) {
      systems.push({ name: 'Firewall', status: 'NOT DEPLOYED', count: 0, color: 'red' });
    }
    if (securityMetrics.siemCount === 0 && securityMetrics.totalSecurityBuildings > 2) {
      systems.push({ name: 'SIEM', status: 'Recommended', count: 0, color: 'yellow' });
    }

    return systems;
  }, [securityMetrics]);

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; border: string; text: string }> = {
      emerald: { bg: 'bg-emerald-900/20', border: 'border-emerald-700/30', text: 'text-emerald-400' },
      cyan: { bg: 'bg-cyan-900/20', border: 'border-cyan-700/30', text: 'text-cyan-400' },
      yellow: { bg: 'bg-yellow-900/20', border: 'border-yellow-700/30', text: 'text-yellow-400' },
      orange: { bg: 'bg-orange-900/20', border: 'border-orange-700/30', text: 'text-orange-400' },
      red: { bg: 'bg-red-900/20', border: 'border-red-700/30', text: 'text-red-400' },
      purple: { bg: 'bg-purple-900/20', border: 'border-purple-700/30', text: 'text-purple-400' }
    };
    return colors[color] || colors.emerald;
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-white/10 pb-4">
        <h3 className="text-xl font-bold text-white uppercase tracking-wider">Security Operations</h3>
        <p className="text-slate-400 mt-2">Monitor threats and manage security posture</p>
      </div>

      {/* Threat Level Dashboard */}
      <div className={`bg-gradient-to-br ${threatLevel.bg} border ${threatLevel.border} rounded-lg p-6`}>
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-center md:text-left">
            <div className={`text-4xl font-black ${threatLevel.color} mb-2`}>
              {threatLevel.level}
            </div>
            <div className="text-sm text-slate-300 uppercase tracking-wider">Current Threat Level</div>
            <div className="text-2xl font-bold text-white mt-2">{stats.threat}%</div>
          </div>
          {simBreakdown && (
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-lg font-bold text-red-400">+{simBreakdown.threat.generated}</div>
                <div className="text-xs text-slate-400">Generated</div>
              </div>
              <div>
                <div className="text-lg font-bold text-emerald-400">-{simBreakdown.threat.mitigated}</div>
                <div className="text-xs text-slate-400">Mitigated</div>
              </div>
              <div>
                <div className={`text-lg font-bold ${simBreakdown.threat.net <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {simBreakdown.threat.net <= 0 ? '' : '+'}{simBreakdown.threat.net.toFixed(1)}
                </div>
                <div className="text-xs text-slate-400">Net/Tick</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Threat History Sparkline */}
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="text-xs text-slate-400 mb-2">Threat History (Last 20 ticks)</div>
          <div className="flex items-end gap-1 h-12">
            {stats.threatHistory.map((value, idx) => (
              <div 
                key={idx}
                className={`flex-1 rounded-t transition-all ${
                  value >= 75 ? 'bg-red-500' :
                  value >= 50 ? 'bg-orange-500' :
                  value >= 25 ? 'bg-yellow-500' :
                  'bg-emerald-500'
                }`}
                style={{ height: `${Math.max(4, value)}%` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Security Building Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-400">{securityMetrics.totalSecurityBuildings}</div>
          <div className="text-xs text-slate-400 uppercase">Security Units</div>
        </div>
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-400">{securityMetrics.cyberBuildings}</div>
          <div className="text-xs text-slate-400 uppercase">Cyber Defense</div>
        </div>
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-cyan-400">{securityMetrics.zeroTrustBuildings}</div>
          <div className="text-xs text-slate-400 uppercase">Zero Trust</div>
        </div>
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">{securityMetrics.advancedSecurityBuildings}</div>
          <div className="text-xs text-slate-400 uppercase">Advanced Sec</div>
        </div>
      </div>

      {/* Security Systems Status */}
      <div className="bg-slate-900/30 border border-white/10 rounded-lg p-6">
        <h4 className="text-lg font-bold text-white mb-4">Security Systems Status</h4>
        {securitySystems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {securitySystems.map((system, idx) => {
              const colors = getColorClasses(system.color);
              return (
                <div key={idx} className={`flex items-center justify-between p-3 ${colors.bg} rounded border ${colors.border}`}>
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${colors.text}`}>{system.name}</span>
                    {system.count > 1 && (
                      <span className="text-xs bg-white/10 px-2 py-0.5 rounded">x{system.count}</span>
                    )}
                  </div>
                  <span className={`text-sm ${colors.text}`}>● {system.status}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <div className="text-4xl mb-2">🛡️</div>
            <p>No security systems deployed yet</p>
            <p className="text-sm mt-2">Build Firewall, SIEM, or other security buildings to protect your infrastructure</p>
          </div>
        )}
      </div>

      {/* Security Recommendations */}
      <div className="bg-slate-900/30 border border-white/10 rounded-lg p-6">
        <h4 className="text-lg font-bold text-white mb-4">Security Recommendations</h4>
        <div className="space-y-2">
          {securityMetrics.firewallCount === 0 && (
            <div className="flex items-center gap-2 p-3 bg-red-900/20 rounded border border-red-700/30">
              <span className="text-red-400">⚠️</span>
              <span className="text-sm text-red-300">Deploy a Firewall to protect your network perimeter</span>
            </div>
          )}
          {securityMetrics.siemCount === 0 && securityMetrics.totalSecurityBuildings > 0 && (
            <div className="flex items-center gap-2 p-3 bg-yellow-900/20 rounded border border-yellow-700/30">
              <span className="text-yellow-400">💡</span>
              <span className="text-sm text-yellow-300">Add SIEM for centralized threat detection and logging</span>
            </div>
          )}
          {securityMetrics.socCount === 0 && securityMetrics.cyberBuildings >= 3 && (
            <div className="flex items-center gap-2 p-3 bg-cyan-900/20 rounded border border-cyan-700/30">
              <span className="text-cyan-400">🔒</span>
              <span className="text-sm text-cyan-300">Consider a SOC for 24/7 security monitoring</span>
            </div>
          )}
          {stats.threat > 50 && (
            <div className="flex items-center gap-2 p-3 bg-orange-900/20 rounded border border-orange-700/30">
              <span className="text-orange-400">🚨</span>
              <span className="text-sm text-orange-300">High threat level! Deploy more security buildings to mitigate risk</span>
            </div>
          )}
          {securityMetrics.totalSecurityBuildings === 0 && securityMetrics.cyberBuildings === 0 && (
            <div className="flex items-center gap-2 p-3 bg-slate-800/50 rounded border border-slate-700/30">
              <span className="text-slate-400">ℹ️</span>
              <span className="text-sm text-slate-300">Start with basic security: AML Unit, Firewall, and Fraud Detection</span>
            </div>
          )}
          {stats.threat <= 10 && securityMetrics.totalSecurityBuildings > 0 && (
            <div className="flex items-center gap-2 p-3 bg-emerald-900/20 rounded border border-emerald-700/30">
              <span className="text-emerald-400">✅</span>
              <span className="text-sm text-emerald-300">Excellent! Your security posture is strong</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurityTab;
